import { validateArray, validateShapesMatch } from '../utils/validation.js';

export function accuracy(yTrue, yPred) {
  validateArray(yTrue, 'yTrue');
  validateArray(yPred, 'yPred');
  validateShapesMatch([yTrue], [yPred], 'yTrue', 'yPred');

  const correct = yTrue.filter((val, i) => val === yPred[i]).length;
  return correct / yTrue.length;
}

export function confusionMatrix(yTrue, yPred) {
  validateArray(yTrue, 'yTrue');
  validateArray(yPred, 'yPred');
  validateShapesMatch([yTrue], [yPred], 'yTrue', 'yPred');

  const combined = yTrue.concat(yPred);
  const classes = Array.from(new Set(combined)).sort();
  const n = classes.length;
  const matrix = Array(n).fill(0).map(() => Array(n).fill(0));

  const classToIndex = {};
  classes.forEach((c, i) => classToIndex[c] = i);

  for (let i = 0; i < yTrue.length; i++) {
    const trueIdx = classToIndex[yTrue[i]];
    const predIdx = classToIndex[yPred[i]];
    matrix[trueIdx][predIdx]++;
  }

  return { matrix, classes };
}

export function precision(yTrue, yPred, options = {}) {
  validateArray(yTrue, 'yTrue');
  validateArray(yPred, 'yPred');
  validateShapesMatch([yTrue], [yPred], 'yTrue', 'yPred');

  const average = options.average || 'binary';
  const posLabel = options.posLabel;

  const { matrix, classes } = confusionMatrix(yTrue, yPred);

  if (average === 'binary') {
    const posIdx = posLabel !== undefined
      ? classes.indexOf(posLabel)
      : 1;

    const tp = matrix[posIdx][posIdx];
    const fp = matrix.reduce((sum, row, i) => i !== posIdx ? sum + row[posIdx] : sum, 0);

    return tp + fp === 0 ? 0 : tp / (tp + fp);
  }

  const precisions = classes.map((_, i) => {
    const tp = matrix[i][i];
    const fp = matrix.reduce((sum, row, j) => j !== i ? sum + row[i] : sum, 0);
    return tp + fp === 0 ? 0 : tp / (tp + fp);
  });

  if (average === 'macro') {
    return precisions.reduce((a, b) => a + b, 0) / precisions.length;
  } else if (average === 'weighted') {
    const weights = classes.map((_, i) => matrix[i].reduce((a, b) => a + b, 0));
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    return precisions.reduce((sum, p, i) => sum + p * weights[i], 0) / totalWeight;
  }

  return precisions;
}

export function recall(yTrue, yPred, options = {}) {
  validateArray(yTrue, 'yTrue');
  validateArray(yPred, 'yPred');
  validateShapesMatch([yTrue], [yPred], 'yTrue', 'yPred');

  const average = options.average || 'binary';
  const posLabel = options.posLabel;

  const { matrix, classes } = confusionMatrix(yTrue, yPred);

  if (average === 'binary') {
    const posIdx = posLabel !== undefined
      ? classes.indexOf(posLabel)
      : 1;

    const tp = matrix[posIdx][posIdx];
    const fn = matrix[posIdx].reduce((sum, val, i) => i !== posIdx ? sum + val : sum, 0);

    return tp + fn === 0 ? 0 : tp / (tp + fn);
  }

  const recalls = classes.map((_, i) => {
    const tp = matrix[i][i];
    const fn = matrix[i].reduce((sum, val, j) => j !== i ? sum + val : sum, 0);
    return tp + fn === 0 ? 0 : tp / (tp + fn);
  });

  if (average === 'macro') {
    return recalls.reduce((a, b) => a + b, 0) / recalls.length;
  } else if (average === 'weighted') {
    const weights = classes.map((_, i) => matrix[i].reduce((a, b) => a + b, 0));
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    return recalls.reduce((sum, r, i) => sum + r * weights[i], 0) / totalWeight;
  }

  return recalls;
}

export function f1Score(yTrue, yPred, options = {}) {
  const p = precision(yTrue, yPred, options);
  const r = recall(yTrue, yPred, options);

  if (Array.isArray(p)) {
    return p.map((prec, i) => {
      const rec = r[i];
      return prec + rec === 0 ? 0 : 2 * prec * rec / (prec + rec);
    });
  }

  return p + r === 0 ? 0 : 2 * p * r / (p + r);
}

export function classificationReport(yTrue, yPred) {
  validateArray(yTrue, 'yTrue');
  validateArray(yPred, 'yPred');
  validateShapesMatch([yTrue], [yPred], 'yTrue', 'yPred');

  const { classes } = confusionMatrix(yTrue, yPred);
  const report = {};

  for (const label of classes) {
    const binaryTrue = yTrue.map(y => y === label ? 1 : 0);
    const binaryPred = yPred.map(y => y === label ? 1 : 0);

    report[label] = {
      precision: precision(binaryTrue, binaryPred, { average: 'binary', posLabel: 1 }),
      recall: recall(binaryTrue, binaryPred, { average: 'binary', posLabel: 1 }),
      f1Score: f1Score(binaryTrue, binaryPred, { average: 'binary', posLabel: 1 }),
      support: yTrue.filter(y => y === label).length
    };
  }

  report.accuracy = accuracy(yTrue, yPred);
  report.macroAvg = {
    precision: precision(yTrue, yPred, { average: 'macro' }),
    recall: recall(yTrue, yPred, { average: 'macro' }),
    f1Score: f1Score(yTrue, yPred, { average: 'macro' })
  };
  report.weightedAvg = {
    precision: precision(yTrue, yPred, { average: 'weighted' }),
    recall: recall(yTrue, yPred, { average: 'weighted' }),
    f1Score: f1Score(yTrue, yPred, { average: 'weighted' })
  };

  return report;
}
